/**
 * UniversalMatchIntro — fullscreen overlay above the Pixi canvas that
 * runs the SAME start sequence for every game in Tile Tavern.
 *
 *     [INTRO 3000ms] -> [TUTORIAL 10000ms] -> [HOLD] -> [COUNTDOWN N*1000ms] -> GO
 *
 * Round 1: full pipeline (intro + tutorial + countdown).
 * Rounds 2+ / reduced-motion: skip the long tutorial popup. The freed
 * lead time becomes a calm "starting soon" HOLD — the client WAITS for
 * the shared countdown anchor instead of racing ahead to GO.
 *
 * ── Why the desync used to happen ───────────────────────────────────
 * The server lands `startsAt` exactly at GO and reserves a FIXED lead
 * window before it (intro 3000ms + tutorial 10000ms, always — see
 * lambdas/ws-router eventModeTiming). Previously this component collapsed
 * the intro/tutorial *durations* to 1ms under prefers-reduced-motion.
 * Because the schedule was wound forward from those (now tiny) durations,
 * a reduced-motion client (common on phones) sequenced its phases off a
 * LOCAL elapsed timer rather than the shared clock — so it could reach the
 * countdown/GO ahead of a full-motion desktop client. Unfair across
 * devices.
 *
 * ── The anchor ──────────────────────────────────────────────────────
 * Everything is now derived BACKWARD from `startsAt`:
 *   countdownStartAt = startsAt - countdownSec*1000      (3-2-1 begins)
 *   GO               = startsAt                          (play begins)
 * These two are IDENTICAL on every client regardless of frame rate,
 * device, or motion preference. Reduced motion only changes whether the
 * tutorial popup is shown — never WHEN the countdown or GO fire. Skipping
 * the tutorial does not start the game earlier; the HOLD phase absorbs the
 * freed time until the shared countdown anchor.
 *
 * Note on clocks: `startsAt` arriving from useLobbySocket is already
 * expressed in THIS client's local clock (it is computed as
 * receiptTime + countdownMs, deliberately discarding the server's absolute
 * timestamp to defeat cross-device clock skew). So comparing against
 * Date.now() directly is correct, and there is no separate clock-sync
 * offset to apply (applying one would double-count the correction).
 *
 * This component REPLACES:
 *   - the old `MatchIntroOverlay` (React 2000ms cinematic).
 *   - per-game `showRoundIntroCard` "tutorial card" (Pixi).
 *   - per-game 3-2-1-GO Pixi/DOM countdowns.
 *   - bespoke `relay-forge` 4-stage explainer overlay.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { GameTile, InputBindings } from '../../lib/api';
import type { LobbyPlayerView } from '@tt/lobby';
import { audio } from '@tt/audio';
import { IconAvatar } from '../characters/IconAvatar';

export const UMI_INTRO_MS = 3000;
export const UMI_TUTORIAL_MS = 10000;
/** GO! holds on screen this long after `startsAt` before the overlay clears. */
export const UMI_GO_MS = 700;

export type UmiPhase = 'intro' | 'tutorial' | 'hold' | 'countdown' | 'go' | 'done';

export interface UmiSchedule {
  introStartAt: number;
  introEndAt: number;
  /** End of the (optional) tutorial popup. Equals introEndAt when the
   *  tutorial is skipped, which makes the tutorial window zero-length. */
  tutorialEndAt: number;
  /** The 3-2-1 countdown begins here. Anchored to `startsAt`. */
  countdownStartAt: number;
  /** GO fires here. The single shared instant every client lines up on. */
  startsAt: number;
  /** Overlay clears here. */
  goEndAt: number;
}

/**
 * Build the start-sequence schedule by winding BACKWARD from the shared
 * `startsAt`, so the countdown + GO are device/motion independent.
 *
 * The server always reserves `UMI_INTRO_MS + UMI_TUTORIAL_MS` of lead time
 * before the countdown regardless of whether THIS client renders the
 * tutorial. We anchor the intro to the start of that reserved lead (≈ match
 * receipt) and let any lead time the intro/tutorial don't consume fall into
 * the HOLD window — that is what keeps a tutorial-skipping client from
 * starting early.
 */
export function buildUmiSchedule(opts: {
  startsAt: number;
  countdownSec: number;
  /** Whether to render the 10s tutorial popup (round 1, full motion). */
  showTutorial: boolean;
}): UmiSchedule {
  const countdownMs = Math.max(0, Math.round(opts.countdownSec * 1000));
  const countdownStartAt = opts.startsAt - countdownMs;
  const reservedLeadMs = UMI_INTRO_MS + UMI_TUTORIAL_MS;
  const introStartAt = countdownStartAt - reservedLeadMs;
  const introEndAt = introStartAt + UMI_INTRO_MS;
  const tutorialEndAt = introEndAt + (opts.showTutorial ? UMI_TUTORIAL_MS : 0);
  return {
    introStartAt,
    introEndAt,
    tutorialEndAt,
    countdownStartAt,
    startsAt: opts.startsAt,
    goEndAt: opts.startsAt + UMI_GO_MS,
  };
}

/** Map an absolute `now` onto the schedule. Pure — unit-testable. */
export function resolveUmiPhase(now: number, s: UmiSchedule): UmiPhase {
  if (now >= s.goEndAt) return 'done';
  if (now >= s.startsAt) return 'go';
  if (now >= s.countdownStartAt) return 'countdown';
  if (now >= s.tutorialEndAt) return 'hold';
  if (now >= s.introEndAt) return 'tutorial';
  return 'intro';
}

interface Props {
  matchId: string;
  game: GameTile | undefined;
  fallbackGameId: string;
  /** Server's `matchStartsAt` — the moment GO fires. */
  startsAt: number;
  /** Pure 3-2-1 countdown length in seconds. From lobby setting. */
  countdownSec: number;
  /** Round 1 plays all 3 phases; rounds 2+ skip the tutorial. */
  isFirstRound: boolean;
  mySlot: number;
  players: LobbyPlayerView[];
  onComplete?: () => void;
}

type Phase = UmiPhase;

export function UniversalMatchIntro({
  matchId, game, fallbackGameId, startsAt, countdownSec,
  isFirstRound, mySlot, players, onComplete,
}: Props) {
  // Reduced-motion (common on mobile): the ANIMATIONS are dropped via CSS
  // (@media prefers-reduced-motion in global.css). Here it also opts the
  // client out of the long, auto-advancing 10s tutorial popup. Crucially
  // this does NOT shorten the warmup — the freed lead time becomes a calm
  // "starting soon" HOLD so this client still reaches the countdown/GO at
  // the same shared instant as a full-motion desktop client.
  const reduce = useMemo(() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  }, []);
  const showTutorial = isFirstRound && !reduce;

  // Single source of truth: wind the whole sequence BACKWARD from the shared
  // `startsAt`. countdownStartAt + GO are therefore identical on every client
  // regardless of device / frame rate / motion preference.
  const schedule = useMemo(
    () => buildUmiSchedule({ startsAt, countdownSec, showTutorial }),
    [startsAt, countdownSec, showTutorial],
  );

  const [phase, setPhase] = useState<Phase>(() => resolveUmiPhase(Date.now(), schedule));
  // Per-frame clock. The rAF loop below writes this every frame so any
  // time-derived UI (the "Get ready…" progress bar) re-renders smoothly.
  // Without it, `setPhase(next)` bails out of re-rendering while the phase
  // is stable (React skips identical state), which froze the get-ready bar
  // at ~0% for the whole 10s tutorial window — the "bar never progresses"
  // bug. `tickLabel` only changes once per second so it couldn't drive a
  // smooth fill either.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const [tickLabel, setTickLabel] = useState<string>('');
  const completeFiredRef = useRef(false);
  const lastTickSecRef = useRef<number>(-1);
  const goFiredRef = useRef(false);
  // Capture the latest onComplete so the rAF effect (keyed only by matchId)
  // always calls the current handler without re-running the loop.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    // Reset per-match guards. Effect re-runs only when matchId changes
    // (parent also re-keys the component, so this is a fresh mount).
    let cancelled = false;
    let raf = 0;
    completeFiredRef.current = false;
    goFiredRef.current = false;
    lastTickSecRef.current = -1;

    const drive = () => {
      if (cancelled) return;
      const now = Date.now();
      const next = resolveUmiPhase(now, schedule);
      setPhase(next);
      // Drive a per-frame re-render so the get-ready bar fills smoothly.
      setNowMs(now);

      if (next === 'countdown') {
        // 3-2-1 numerals derived from the shared `startsAt` — never from a
        // local elapsed timer — so the per-second flips line up with GO.
        const sec = Math.ceil((schedule.startsAt - now) / 1000);
        if (sec !== lastTickSecRef.current) {
          // First-paint silent; otherwise tick exactly once per integer second.
          if (lastTickSecRef.current !== -1 && sec > 0) {
            try { audio.sfx.countdownTick?.(); } catch { /* */ }
          }
          lastTickSecRef.current = sec;
          setTickLabel(String(Math.max(1, sec)));
        }
      } else if (next === 'go') {
        // GO window. Fire chime exactly once via ref guard — closure-captured
        // `phase` would be stale here and cause the chime to repeat every frame.
        if (!goFiredRef.current) {
          goFiredRef.current = true;
          setTickLabel('GO!');
          try { audio.sfx.countdownGo?.(); } catch { /* */ }
        }
      } else if (next === 'done') {
        if (!completeFiredRef.current) {
          completeFiredRef.current = true;
          try { onCompleteRef.current?.(); } catch { /* */ }
        }
        return; // stop driving
      }
      raf = requestAnimationFrame(drive);
    };
    raf = requestAnimationFrame(drive);
    return () => { cancelled = true; if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, schedule]);

  if (phase === 'done') return null;

  const gameName = game?.name ?? fallbackGameId;
  const pitch = game?.pitch ?? '';
  const tutorialHeadline = game?.tutorial?.headline ?? game?.pitch ?? '';
  const tutorialTip = game?.tutorial?.tip ?? '';
  const category = game?.category ?? 'reflex';
  const sortedPlayers = [...players].sort((a, b) => a.slot - b.slot);

  // Get-ready progress bar: fills 0→100% across the ENTIRE warm-up window
  // that precedes the 3-2-1 countdown — [introEndAt .. countdownStartAt] —
  // then hands off to the countdown numerals and GO. Anchored to the shared
  // `startsAt`, so it advances at the same rate on every client. Computed
  // from the per-frame `nowMs` state so it re-renders smoothly instead of
  // freezing at the value captured when the phase first rendered.
  const getReadyPct = (() => {
    const start = schedule.introEndAt;
    const end = schedule.countdownStartAt;
    if (end <= start) return 100;
    return Math.max(0, Math.min(100, ((nowMs - start) / (end - start)) * 100));
  })();

  const { pcLines, mobileLines } = derivedControls(game?.inputBindings, game?.controls);

  return (
    <div className={`umi-overlay umi-phase-${phase}`} aria-hidden="true" role="presentation">
      <div className="umi-vignette" />

      {/* Phase 1: INTRO */}
      {phase === 'intro' && (
        <div className="umi-intro" data-cat={category}>
          <div className="umi-intro-kicker">Now playing</div>
          <h1 className="umi-intro-title" data-cat={category}>{gameName}</h1>
          {pitch && <p className="umi-intro-purpose">{pitch}</p>}
          <div className="umi-intro-meta">
            <span className="umi-meta-pill">{category}</span>
            {game?.durationSec ? <span className="umi-meta-pill">{game.durationSec}s</span> : null}
            {game?.chaoticness ? <span className="umi-meta-pill">{'⚡'.repeat(Math.min(5, game.chaoticness))}</span> : null}
          </div>
          <div className="umi-intro-players">
            {sortedPlayers.map((p, i) => (
              <div
                key={p.charId}
                className={`umi-intro-card${p.slot === mySlot ? ' is-me' : ''}`}
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <IconAvatar icon={p.icon} size={48} alt={p.name} />
                <strong>{p.name}</strong>
                <small>P{p.slot + 1}</small>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase 2: TUTORIAL — round 1 only */}
      {phase === 'tutorial' && (
        <div className="umi-tutorial" data-cat={category}>
          <div className="umi-tutorial-heading">How to play</div>
          <h2 className="umi-tutorial-title">{gameName}</h2>
          <div className="umi-tutorial-cols">
            <div className="umi-tutorial-col umi-tutorial-col-pc">
              <div className="umi-tutorial-col-label">PC</div>
              <ul className="umi-tutorial-list">
                {pcLines.length === 0 && <li className="muted">— no keyboard —</li>}
                {pcLines.map((line, i) => (
                  <li key={i}>{renderControlLine(line)}</li>
                ))}
              </ul>
            </div>
            <div className="umi-tutorial-col umi-tutorial-col-mobile">
              <div className="umi-tutorial-col-label">Mobile</div>
              <ul className="umi-tutorial-list">
                {mobileLines.length === 0 && <li className="muted">— no touch hint —</li>}
                {mobileLines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="umi-tutorial-objective">
            <div className="umi-tutorial-objective-label">Objective</div>
            <div className="umi-tutorial-objective-headline">{tutorialHeadline}</div>
            {tutorialTip && <div className="umi-tutorial-objective-tip">{tutorialTip}</div>}
          </div>
          <div className="umi-tutorial-progress" aria-hidden="true">
            <div className="umi-tutorial-progress-fill" style={{ width: `${getReadyPct}%` }} />
            <span className="umi-tutorial-progress-label">Get ready…</span>
          </div>
        </div>
      )}

      {/* Phase 2.5: HOLD — shown when the intro/tutorial finished before the
          shared countdown anchor (reduced-motion clients skip the long
          tutorial; rounds 2+ skip it entirely). The client WAITS here rather
          than starting early, so GO stays locked to the server's `startsAt`. */}
      {phase === 'hold' && (
        <div className="umi-hold" data-cat={category}>
          <div className="umi-intro-kicker">Get ready</div>
          <h2 className="umi-hold-title">{gameName}</h2>
          {tutorialHeadline && <p className="umi-hold-objective">{tutorialHeadline}</p>}
          <div className="umi-hold-status">Starting soon…</div>
          {/* Same 0→100% get-ready bar the tutorial phase shows, so rounds
              2+ and reduced-motion clients (which skip the tutorial and land
              straight in HOLD) still see a filling bar rather than a frozen
              "Starting soon…". */}
          <div className="umi-tutorial-progress umi-hold-progress" aria-hidden="true">
            <div className="umi-tutorial-progress-fill" style={{ width: `${getReadyPct}%` }} />
            <span className="umi-tutorial-progress-label">Get ready…</span>
          </div>
        </div>
      )}

      {/* Phase 3: COUNTDOWN (3-2-1) */}
      {phase === 'countdown' && (
        <div className="umi-countdown">
          <div className="umi-countdown-numeral" data-num={tickLabel}>{tickLabel}</div>
          <div className="umi-countdown-sub">{gameName}</div>
        </div>
      )}

      {/* Phase 4: GO! (brief) */}
      {phase === 'go' && (
        <div className="umi-go">
          <div className="umi-go-numeral">GO!</div>
        </div>
      )}
    </div>
  );
}

// ── Control derivation ───────────────────────────────────────────────

function derivedControls(
  bindings: InputBindings | undefined,
  controls: string[] | undefined,
): { pcLines: ControlLine[]; mobileLines: string[] } {
  const pc: ControlLine[] = [];
  const mobile: string[] = [];

  if (bindings) {
    if (bindings.primary) {
      const { label, key } = bindings.primary;
      if (key && key.length > 0) pc.push({ label, keys: key });
      mobile.push(label);
    }
    if (bindings.alt) {
      for (const a of bindings.alt) {
        if (a.key && a.key.length > 0) pc.push({ label: a.label, keys: a.key });
        else mobile.push(a.label);
      }
    }
  }

  // Fallback: if neither column got anything from inputBindings, splay
  // the manifest.controls array into the mobile column (these are mostly
  // gesture verbs like "tap" / "draw").
  if (pc.length === 0 && mobile.length === 0 && controls && controls.length > 0) {
    for (const c of controls) mobile.push(c);
  }
  return { pcLines: pc, mobileLines: mobile };
}

interface ControlLine {
  label: string;
  keys: string[];
}

function renderControlLine(line: ControlLine) {
  const pretty = line.keys.map(prettyKey);
  return (
    <span className="umi-control-line">
      <span className="umi-control-label">{line.label}</span>
      <span className="umi-control-keys">
        {pretty.map((k, i) => (
          <kbd key={i} className="umi-kbd">{k}</kbd>
        ))}
      </span>
    </span>
  );
}

function prettyKey(code: string): string {
  switch (code) {
    case 'Space': return 'Space';
    case 'Enter': return 'Enter';
    case 'ArrowUp': return '↑';
    case 'ArrowDown': return '↓';
    case 'ArrowLeft': return '←';
    case 'ArrowRight': return '→';
    case 'Digit1': return '1';
    case 'Digit2': return '2';
    case 'Digit3': return '3';
    case 'Digit4': return '4';
    case 'ShiftLeft':
    case 'ShiftRight': return 'Shift';
    case 'ControlLeft':
    case 'ControlRight': return 'Ctrl';
    default:
      if (code.startsWith('Key')) return code.slice(3);
      return code;
  }
}
